/**
 * UX review 2026-07-22 (1.1 / 1.2): find the most recent calendar window
 * (same length as the current range) that contains any loaded activity.
 */

const clampDateString = (value) => {
  if (!value) return null;
  const date = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
};

const extractItemDate = (item) => clampDateString(
  item?.date ||
  item?.entry_date ||
  item?.watched_at ||
  item?.event_date ||
  item?.played_at ||
  item?.route_date
);

const extractRouteDate = (route) => {
  const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
  return clampDateString(firstPoint.route_date || firstPoint.date);
};

const collectDatesFromList = (list, dateFn) => {
  const dates = [];
  (list || []).forEach((item) => {
    const day = dateFn(item);
    if (day) dates.push(day);
  });
  return dates;
};

const collectDatesFromAggregate = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value) || !value._aggregate) {
    return collectDatesFromList(value, extractItemDate);
  }
  return collectDatesFromList(value.byDay || [], (row) => clampDateString(row.date));
};

/** All YYYY-MM-DD activity dates across loaded map-data sources. */
export function collectActivityDates(data = {}) {
  return [
    ...collectDatesFromList(data.checkins, extractItemDate),
    ...collectDatesFromList(data.health, extractRouteDate),
    ...collectDatesFromList(data.moves, extractRouteDate),
    ...collectDatesFromList(data.googleMaps, extractRouteDate),
    ...collectDatesFromAggregate(data.movies),
    ...collectDatesFromAggregate(data.shows),
    ...collectDatesFromList(data.diary, extractItemDate),
    ...collectDatesFromList(data.concerts, extractItemDate),
    ...collectDatesFromAggregate(data.spotify),
    ...collectDatesFromAggregate(data.appleMusic),
    ...collectDatesFromList(data.photos, extractItemDate),
    ...(data.music && typeof data.music === 'object' && data.music._aggregate
      ? collectDatesFromAggregate(data.music)
      : []),
  ].sort();
}

const addDays = (isoDate, delta) => {
  const date = new Date(`${isoDate}T12:00:00`);
  date.setDate(date.getDate() + delta);
  return date.toISOString().slice(0, 10);
};

const spanDaysForRange = (range = {}) => {
  const start = clampDateString(range.startDate);
  const end = clampDateString(range.endDate);
  if (!start || !end || end < start) return 30;
  const startMs = new Date(`${start}T12:00:00`).getTime();
  const endMs = new Date(`${end}T12:00:00`).getTime();
  return Math.max(1, Math.round((endMs - startMs) / 86400000) + 1);
};

/**
 * @param {object} data - loaded dataset (checkins, diary, routes, …)
 * @param {{ startDate?: string, endDate?: string }} currentRange
 * @returns {{ startDate: string, endDate: string } | null}
 */
export function findLastPeriodWithData(data = {}, currentRange = {}) {
  const dates = collectActivityDates(data);
  if (dates.length === 0) return null;

  const endDate = dates[dates.length - 1];
  const spanDays = spanDaysForRange(currentRange);
  const startDate = addDays(endDate, -(spanDays - 1));

  const currentStart = clampDateString(currentRange.startDate);
  const currentEnd = clampDateString(currentRange.endDate);
  if (currentStart === startDate && currentEnd === endDate) return null;

  return { startDate, endDate };
}

/** True when Insights has no positive metric for the selected period. */
export function isAllZeroInsights(insights = {}) {
  const cities = insights.cities || [];
  const distanceByType = insights.distanceByType || {};
  const hasDistance = Object.values(distanceByType).some((km) => Number(km) > 0);
  const music = insights.music || {};
  const entertainment = insights.entertainment || {};
  const concerts = insights.concerts || {};
  const streak = insights.streak || {};
  const topVenues = insights.topVenues || [];
  const mostActiveDays = insights.mostActiveDays || [];

  return (
    cities.length === 0 &&
    !hasDistance &&
    topVenues.length === 0 &&
    mostActiveDays.length === 0 &&
    !(Number(music.totalPlays) > 0) &&
    !(Number(entertainment.totalMovies) > 0 || Number(entertainment.totalEpisodes) > 0) &&
    !(Number(concerts.totalConcerts) > 0) &&
    !(Number(streak.totalDays) > 0)
  );
}
